import React, { useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, ShieldCheck, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { BRAND } from "../lib/mockData";
import { api, auth } from "../lib/api";

// Real OTP login (dev mode) — Customer & Attorney
export default function OtpLogin({
  role = "Customer",
  roleKey = "customer", // "customer" | "attorney"
  subtitle,
  redirectTo = "/",
  accentIcon: AccentIcon = ShieldCheck,
  sideImage,
  sidePoints = [],
}) {
  const navigate = useNavigate();
  const [step, setStep] = useState("phone");
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [devOtp, setDevOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const inputs = useRef([]);

  const handleOtp = (i, val) => {
    if (!/^\d?$/.test(val)) return;
    const copy = [...otp];
    copy[i] = val;
    setOtp(copy);
    if (val && i < 5) inputs.current[i + 1]?.focus();
  };

  const handleKey = (i, e) => {
    if (e.key === "Backspace" && !otp[i] && i > 0) inputs.current[i - 1]?.focus();
  };

  const sendOtp = async () => {
    if (phone.length !== 10) return;
    setLoading(true);
    try {
      const r = await api.requestOtp(phone, roleKey);
      setDevOtp(r.dev_otp || "");
      setStep("otp");
      toast.success(`OTP sent to +91 ${phone}`, { description: r.dev_otp ? `Dev OTP: ${r.dev_otp} (or use 123456)` : "" });
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  const verify = async () => {
    const code = otp.join("");
    if (code.length !== 6) return;
    setLoading(true);
    try {
      const r = await api.verifyOtp(phone, code, roleKey);
      auth.set(r.token, r.user);
      toast.success(`Welcome, ${r.user.name || r.user.phone}`);
      navigate(redirectTo);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2" data-testid="otp-login-page">
      {/* LEFT — brand / illustration */}
      <div className="hidden lg:flex relative surface-deep text-white flex-col justify-between p-12 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-[500px] h-[500px] blob bg-[#7CC242]" />
        <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] blob bg-[#0E6E5E]" />

        <Link to="/" className="relative flex items-center gap-3 w-fit">
          <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-[#0E6E5E] to-[#0A3B36] flex items-center justify-center ring-1 ring-white/10">
            <span className="font-display font-black text-2xl text-[#7CC242]">U</span>
          </div>
          <div>
            <div className="font-display font-extrabold text-2xl">{BRAND.name}</div>
            <div className="text-[10px] tracking-[0.2em] uppercase text-[#7CC242] font-semibold mt-0.5">Securing Ideas · Nurturing Success</div>
          </div>
        </Link>

        <div className="relative">
          {sideImage && <img src={sideImage} alt="" className="rounded-3xl aspect-[16/10] object-cover w-full mb-8 opacity-90" />}
          <div className="chip chip-gold inline-flex mb-4"><AccentIcon size={13}/> {role} Portal</div>
          <h2 className="font-display font-extrabold text-4xl leading-tight mb-6">Welcome to your <br/>{role.toLowerCase()} workspace.</h2>
          <div className="space-y-3">
            {sidePoints.map((p) => (
              <div key={p} className="flex items-center gap-3 text-white/80">
                <div className="w-6 h-6 rounded-full bg-[#7CC242] flex items-center justify-center flex-shrink-0">
                  <Check size={13} className="text-[#0A2520]"/>
                </div>
                <span>{p}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="relative text-xs text-white/40">© 2025 Udhyak Technologies Pvt Ltd · Secure OTP login</div>
      </div>

      {/* RIGHT — form */}
      <div className="flex items-center justify-center p-6 sm:p-12 surface-cream">
        <div className="w-full max-w-md">
          <Link to="/" className="lg:hidden flex items-center gap-2.5 mb-10">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-[#0E6E5E] to-[#0A2520] flex items-center justify-center">
              <span className="font-display font-black text-xl text-[#7CC242]">U</span>
            </div>
            <div className="font-display font-extrabold text-xl text-[#0A2520]">{BRAND.name}</div>
          </Link>

          <div className="chip mb-4 inline-flex"><AccentIcon size={13}/> {role} Login</div>

          {step === "phone" && (
            <div className="fade-up" data-testid="otp-step-phone">
              <h1 className="font-display font-extrabold text-4xl text-[#0A2520] mb-2">Log in or sign up</h1>
              <p className="text-[#5A6B66] mb-8">{subtitle || "Enter your mobile number to receive a one-time password."}</p>

              <label className="block font-display text-sm font-semibold text-[#0A2520] mb-2">Mobile number</label>
              <div className="flex gap-2 mb-6">
                <div className="input w-20 flex items-center justify-center font-semibold text-[#0A2520] flex-shrink-0">+91</div>
                <input
                  value={phone}
                  onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                  placeholder="98765 43210"
                  className="input flex-1"
                  data-testid="otp-phone-input"
                  inputMode="numeric"
                  onKeyDown={(e) => e.key === "Enter" && sendOtp()}
                />
              </div>

              <button
                onClick={sendOtp}
                disabled={phone.length !== 10 || loading}
                className="btn-primary w-full justify-center disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="otp-send-btn"
              >
                {loading ? <Loader2 size={16} className="animate-spin"/> : <>Send OTP <ArrowRight size={16}/></>}
              </button>

              <div className="flex items-center gap-2 mt-6 text-xs text-[#5A6B66]">
                <ShieldCheck size={13} className="text-[#0E6E5E]"/>
                Your number is used only for secure login. We never share it.
              </div>

              <p className="text-sm text-[#5A6B66] mt-8 text-center">
                By continuing you agree to Udhyak's <span className="text-[#0E6E5E] font-semibold cursor-pointer">Terms</span> &amp; <span className="text-[#0E6E5E] font-semibold cursor-pointer">Privacy Policy</span>.
              </p>
            </div>
          )}

          {step === "otp" && (
            <div className="fade-up" data-testid="otp-step-code">
              <button onClick={() => setStep("phone")} className="flex items-center gap-1.5 text-sm text-[#5A6B66] hover:text-[#0E6E5E] mb-6" data-testid="otp-change-number">
                <ArrowLeft size={14}/> Change number
              </button>
              <h1 className="font-display font-extrabold text-4xl text-[#0A2520] mb-2">Enter OTP</h1>
              <p className="text-[#5A6B66] mb-2">We sent a 6-digit code to <span className="font-semibold text-[#0A2520]">+91 {phone}</span></p>
              <div className="chip chip-accent inline-flex mb-8 text-xs" data-testid="otp-dev-hint">
                Dev OTP: <span className="font-mono ml-1">{devOtp || "—"}</span> · or use <span className="font-mono ml-1">123456</span>
              </div>

              <div className="flex gap-2 mb-6">
                {otp.map((d, i) => (
                  <input
                    key={i}
                    ref={(el) => (inputs.current[i] = el)}
                    value={d}
                    onChange={(e) => handleOtp(i, e.target.value)}
                    onKeyDown={(e) => handleKey(i, e)}
                    maxLength={1}
                    inputMode="numeric"
                    className="w-full h-14 text-center text-xl font-bold rounded-xl border border-[#DDE5DD] bg-white focus:border-[#0E6E5E] focus:ring-2 focus:ring-[#0E6E5E]/15 outline-none"
                    data-testid={`otp-digit-${i}`}
                  />
                ))}
              </div>

              <button
                onClick={verify}
                disabled={otp.join("").length !== 6 || loading}
                className="btn-primary w-full justify-center disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="otp-verify-btn"
              >
                {loading ? <Loader2 size={16} className="animate-spin"/> : <>Verify &amp; Continue <ArrowRight size={16}/></>}
              </button>

              <button onClick={sendOtp} className="text-sm text-[#0E6E5E] font-semibold mt-6 w-full text-center" data-testid="otp-resend">
                Didn't get the code? Resend
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
